const SLOW_CONNECTION_MS = 10000;
const CONTINUE_ANYWAY_MS = 30000;
const SUPPORTING_IMAGE_WAIT_MS = 2500;
const MIN_LOADER_VISIBLE_MS = 500;
const SLOW_CONNECTION_MESSAGE = "Connection is slow. Still loading page assets...";
let slowConnectionTimer;
let continueAnywayTimer;

const clearLoaderTimers = () => {
  window.clearTimeout(slowConnectionTimer);
  window.clearTimeout(continueAnywayTimer);
  window.clearTimeout(window.__avadLoaderSlowTimer);
  window.clearTimeout(window.__avadLoaderContinueTimer);
};

const startLoaderTimers = () => {
  const loader = document.getElementById("loader");
  const loaderMessage = document.getElementById("loader-message");

  clearLoaderTimers();

  slowConnectionTimer = window.setTimeout(() => {
    loader?.classList.add("is-slow");
    if (loaderMessage && !loader?.classList.contains("is-language-change")) {
      loaderMessage.textContent = SLOW_CONNECTION_MESSAGE;
    }
  }, SLOW_CONNECTION_MS);

  continueAnywayTimer = window.setTimeout(() => {
    loader?.classList.add("can-continue");
  }, CONTINUE_ANYWAY_MS);
};

const handleContinueAnyway = () => {
  hidePageLoader();
};

export function showPageLoader({ languageMessage = false } = {}) {
  const loader = document.getElementById("loader");
  const continueButton = document.getElementById("loader-continue");
  if (!loader) return;

  if (!languageMessage) {
    const loaderMessage = document.getElementById("loader-message");
    loader.classList.remove("is-language-change");
    loader.classList.remove("is-slow");
    loader.classList.remove("can-continue");
    if (loaderMessage) {
      loaderMessage.textContent = "";
    }
  }

  loader.style.display = "";
  loader.classList.remove("hide");
  continueButton?.removeEventListener("click", handleContinueAnyway);
  continueButton?.addEventListener("click", handleContinueAnyway);
  document.body.classList.add("ovf-hidden");
  startLoaderTimers();
}

export function hidePageLoader() {
  const loader = document.getElementById("loader");
  const loaderMessage = document.getElementById("loader-message");
  const continueButton = document.getElementById("loader-continue");

  clearLoaderTimers();
  continueButton?.removeEventListener("click", handleContinueAnyway);
  loader?.classList.add("hide");
  loader?.classList.remove("is-language-change");
  loader?.classList.remove("is-slow");
  loader?.classList.remove("can-continue");
  document.body.classList.remove("ovf-hidden");

  if (loaderMessage) {
    loaderMessage.textContent = "";
  }

  if (loader) {
    window.setTimeout(() => {
      if (loader.classList.contains("hide")) {
        loader.style.display = "none";
      }
    }, 340);
  }
}

const waitForImage = (image) => {
  if (!image.currentSrc && !image.src) return Promise.resolve();

  if (image.complete && image.naturalWidth > 0) {
    return image.decode?.().catch(() => undefined) ?? Promise.resolve();
  }

  return new Promise((resolve) => {
    const cleanup = () => {
      image.removeEventListener("load", handleDone);
      image.removeEventListener("error", handleDone);
    };
    const handleDone = () => {
      cleanup();
      image.decode?.().catch(() => undefined).finally(resolve);
    };

    image.addEventListener("load", handleDone, { once: true });
    image.addEventListener("error", handleDone, { once: true });
  });
};

const isCriticalImage = (image) => {
  if (image.dataset.criticalImage === "true") return true;

  const rect = image.getBoundingClientRect();
  const isInInitialViewport = rect.top < window.innerHeight * 1.25 && rect.bottom > -80;

  return (
    isInInitialViewport ||
    image.fetchPriority === "high" ||
    image.getAttribute("fetchpriority") === "high" ||
    image.loading === "eager"
  );
};

const waitWithTimeout = (promise, timeoutMs) =>
  Promise.race([
    promise,
    new Promise((resolve) => window.setTimeout(resolve, timeoutMs)),
  ]);

export function waitForCurrentPageAssets() {
  return new Promise((resolve) => {
    const startedAt = performance.now();
    let isResolved = false;

    const finish = () => {
      if (isResolved) return;
      isResolved = true;

      const elapsed = performance.now() - startedAt;
      const remaining = Math.max(0, MIN_LOADER_VISIBLE_MS - elapsed);
      window.setTimeout(resolve, remaining);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const allImages = Array.from(document.querySelectorAll("#root img"));
        const criticalImages = allImages.filter((image) => image.dataset.criticalImage === "true");
        const supportingImages = allImages
          .filter(isCriticalImage)
          .filter((image) => image.dataset.criticalImage !== "true");

        Promise.all([
          Promise.all(criticalImages.map(waitForImage)),
          waitWithTimeout(Promise.all(supportingImages.map(waitForImage)), SUPPORTING_IMAGE_WAIT_MS),
        ]).finally(finish);
      });
    });
  });
}
